import { useEffect, useState } from 'react';
import { GetTeamGroups, type TeamGroup } from '@/apis/teamGroups';
import { parseTeamGroups } from '@/hooks/useTeamGroups';
import { getTeamsDDLOFFeature } from '@/common/utils';
import { resolveTeams, ResolvedTeams } from './monitorTeams';

// getTeamsDDLOFFeature turns a failed request into an empty list, so an empty
// list may be a network blip: keep retrying rather than strand the TV. A
// non-empty list that lacks a requested team is a real answer (a typo or a
// team the user cannot see), so that is not retried. A failed group lookup
// always is, since names then resolve against teams alone.
export const TEAMS_RETRY_MS = 60 * 1000;

/** Team groups, so `?team=` can name one; null when the request failed. */
const loadTeamGroups = (token: string): Promise<TeamGroup[] | null> =>
  GetTeamGroups(token)
    .then((res): TeamGroup[] => {
      // Validated: an older backend returns plain teams for view=groups.
      return parseTeamGroups(res?.data?.data?.data);
    })
    .catch((): null => null);

export type MonitorTeamsState =
  | { status: 'all' }
  | { status: 'loading' }
  | ({ status: 'ready' } & ResolvedTeams);

/** The address named teams and none of them matched. */
export const isNoTeamMatched = (
  state: MonitorTeamsState
  // Narrows to "ready with no teams" only, so the false branch keeps ready
  // states that did match.
): state is {
  status: 'ready';
  teams: never[];
  labels: string[];
  missing: string[];
  suggestions: string[];
} => state.status === 'ready' && state.teams.length === 0;

/**
 * Resolves `?team=` values against the Shipment teams the user can see (the
 * same list the dashboard's team filter offers); a group's name or id
 * resolves to its teams. No values = every team.
 */
export const useMonitorTeams = (
  token: string | null | undefined,
  requested: string[]
): MonitorTeamsState => {
  const requestKey = requested.join('\n');
  const [state, setState] = useState<MonitorTeamsState>(() =>
    requested.length ? { status: 'loading' } : { status: 'all' }
  );

  useEffect(() => {
    const values = requestKey ? requestKey.split('\n') : [];
    if (!values.length) {
      setState({ status: 'all' });
      return undefined;
    }
    if (!token) return undefined;
    let active = true;
    let retryId: number | undefined;
    const load = () => {
      Promise.all([
        getTeamsDDLOFFeature(token, 'Shipment'),
        loadTeamGroups(token),
      ])
        .then(([options, groups]) => {
          if (!active) return;
          const available = Array.isArray(options) ? options : [];
          const resolved = resolveTeams(values, available, groups ?? []);
          setState({ status: 'ready', ...resolved });
          // Without groups a value can go unmatched, or partially match a
          // different team ("Ops" → "Ops Europe"), so retry either way.
          if (available.length === 0 || groups === null) {
            retryId = window.setTimeout(load, TEAMS_RETRY_MS);
          }
        })
        // Neither lookup rejects today; if one ever does, try again later.
        .catch(() => {
          if (active) retryId = window.setTimeout(load, TEAMS_RETRY_MS);
        });
    };
    setState({ status: 'loading' });
    load();
    return () => {
      active = false;
      window.clearTimeout(retryId);
    };
  }, [token, requestKey]);

  return state;
};
